import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useStateValue } from '@State'
import { Button } from '@CommonComponents/buttons'
import { saveAnswer } from '../../api'
import { FeedbackDisplay } from './utils/FeedbackDisplay'
import { TabSplitLayout } from './utils/TabSplitLayout'
import { SaveIndicator } from './utils/SaveIndicator'
import { FEEDBACK_LABELS, getOptionFeedbackStatus } from './utils/feedbackStyles'
import { HotspotStage, Pin, REGION_STYLES, RegionLabel, RegionShape } from '../../../hotspot/HotspotStage'
import { clampPercent, hitRegion, pointerPercent, round2 } from '../../../hotspot/geometry'

const BADGE_FAMILY = { correct: 'success', incorrect: 'danger', missed: 'warning' }

const KEY_STEP = 1
const KEY_STEP_LARGE = 5

const CROSSHAIR_LINE = { position: 'absolute', backgroundColor: 'var(--focus)', pointerEvents: 'none' }
const CROSSHAIR_RING = {
  position: 'absolute', width: 16, height: 16, marginLeft: -8, marginTop: -8, borderRadius: '50%',
  border: '2px solid var(--focus)', pointerEvents: 'none',
}
const LIST_ROW = { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }

export function parseSelections(answer) {
  if (!answer) return []
  try {
    const parsed = JSON.parse(answer)
    return Array.isArray(parsed) ? parsed.filter(s => s && typeof s === 'object') : []
  } catch {
    return []
  }
}

const describePin = pin => `${Math.round(pin.x)}% across, ${Math.round(pin.y)}% down`

export const HotspotQuestion = ({ question, renderTabs = true }) => {
  const [state, dispatch] = useStateValue()
  const { coursenum, activitySubmissionId, savingGuids, readonly, showFeedback } = state
  const { showAnswer } = state.feedbackSettings
  const { imageUrl, imageAlt, regions, maxSelections } = question.data
  const isSaving = savingGuids.has(question.guid)

  const [selections, setSelections] = useState(() => parseSelections(question.answer))
  const [crosshair, setCrosshair] = useState({ x: 50, y: 50 })
  const [keyboardActive, setKeyboardActive] = useState(false)
  const [listOpen, setListOpen] = useState(() => parseSelections(question.answer).some(s => s.region))
  const [message, setMessage] = useState('')
  const stageRef = useRef(null)

  // Region shapes only arrive once the answer may be shown.
  const reviewing = !!(showFeedback && showAnswer && question.correctAnswer)
  const interactive = !readonly && !reviewing
  const atMax = selections.length >= maxSelections
  const labelFor = useMemo(() => Object.fromEntries(regions.map(r => [r.guid, r.label])), [regions])

  useEffect(() => {
    dispatch({
      type: 'SET_FULLY_ANSWERED', guid: question.guid,
      value: parseSelections(question.answer).length >= 1,
    })
  }, [question.answer, question.guid])

  const save = useCallback(async updated => {
    setSelections(updated)
    const answerJson = JSON.stringify(updated)
    dispatch({ type: 'SET_ANSWER', guid: question.guid, answer: answerJson })
    dispatch({ type: 'SAVE_START', guid: question.guid })
    try {
      const response = await saveAnswer(coursenum, activitySubmissionId, question.guid, answerJson)
      dispatch({ type: 'SAVE_SUCCESS', guid: question.guid, payload: response.data })
    } catch (e) {
      dispatch({ type: 'SAVE_ERROR', guid: question.guid })
    }
  }, [coursenum, activitySubmissionId, question.guid, dispatch])

  const addSelection = useCallback((selection, description) => {
    if (!interactive || isSaving) return
    if (atMax && maxSelections > 1) {
      setMessage(`You can place at most ${maxSelections}. Remove one to choose another.`)
      return
    }
    // With a single selection, a new one replaces the old.
    const updated = maxSelections === 1 ? [selection] : [...selections, selection]
    setMessage(`Selected ${description}.`)
    save(updated)
  }, [interactive, isSaving, atMax, maxSelections, selections, save])

  const removeSelection = useCallback(index => {
    if (!interactive || isSaving) return
    const removed = selections[index]
    setMessage(`Removed ${removed.region ? labelFor[removed.region] : `pin ${index + 1}`}.`)
    save(selections.filter((_, i) => i !== index))
  }, [interactive, isSaving, selections, labelFor, save])

  const placePin = useCallback(pin => addSelection(pin, `a pin at ${describePin(pin)}`), [addSelection])

  const handleStageClick = useCallback(e => {
    // detail is 0 for clicks synthesized from a key press (base_template.jsm turns Enter on any
    // [tabindex] element into a click); Enter is handled in keydown at the crosshair instead.
    if (!interactive || e.detail === 0) return
    setKeyboardActive(false)
    placePin(pointerPercent(e, stageRef.current))
  }, [interactive, placePin])

  const handleStageKeyDown = useCallback(e => {
    if (!interactive) return
    const step = e.shiftKey ? KEY_STEP_LARGE : KEY_STEP
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }
    if (moves[e.key]) {
      e.preventDefault()
      setKeyboardActive(true)
      const [dx, dy] = moves[e.key]
      setCrosshair(c => ({ x: round2(clampPercent(c.x + dx)), y: round2(clampPercent(c.y + dy)) }))
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setKeyboardActive(true)
      placePin({ ...crosshair })
    }
  }, [interactive, crosshair, placePin])

  const toggleListRegion = useCallback(guid => {
    const index = selections.findIndex(s => s.region === guid)
    if (index >= 0) removeSelection(index)
    else addSelection({ region: guid }, labelFor[guid])
  }, [selections, removeSelection, addSelection, labelFor])

  const pinStatus = pin => {
    if (!reviewing) return 'default'
    const region = hitRegion(regions, pin.x, pin.y)
    return region && region.correct ? 'correct' : 'incorrect'
  }

  const correctSet = useMemo(() => new Set(reviewing ? question.correctAnswer : []), [reviewing, question.correctAnswer])
  const listSelected = new Set(selections.filter(s => s.region).map(s => s.region))

  if (!imageUrl) {
    return <div className="alert alert-warning">This hotspot question has no image yet.</div>
  }

  return (
    <>
      <FeedbackDisplay question={question} />
      <div className="position-relative">
        <SaveIndicator questionGuid={question.guid} />
      </div>
      <TabSplitLayout question={question} renderTabs={renderTabs}>
        <div className="d-block text-left w-100">
          <div
            role="heading"
            aria-level={3}
            className="mb-2"
            dangerouslySetInnerHTML={{ __html: question.text }}
          />
          <div aria-live="polite" className="sr-only">{message}</div>

          <HotspotStage
            imageUrl={imageUrl}
            imageAlt={imageAlt}
            stageRef={stageRef}
            stageProps={interactive ? {
              tabIndex: 0,
              role: 'application',
              'aria-label': `${imageAlt}. Click to place a pin, or use the arrow keys to move the crosshair (Shift for larger steps) and Enter to place a pin.`,
              onClick: handleStageClick,
              onKeyDown: handleStageKeyDown,
              onBlur: () => setKeyboardActive(false),
              style: { cursor: 'crosshair', outlineOffset: 2 },
            } : {}}
            overlay={reviewing && regions.map(region => (
              <RegionShape
                key={region.guid}
                region={region}
                style={region.correct ? REGION_STYLES.correct : REGION_STYLES.distractor}
              />
            ))}
          >
            {reviewing && regions.map(region => <RegionLabel key={region.guid} region={region} />)}
            {/* The keyboard crosshair sits under the pins so it never hides their numbers. */}
            {interactive && keyboardActive && (
              <>
                <span style={{ ...CROSSHAIR_LINE, left: `${crosshair.x}%`, top: 0, bottom: 0, width: 1 }} />
                <span style={{ ...CROSSHAIR_LINE, top: `${crosshair.y}%`, left: 0, right: 0, height: 1 }} />
                <span style={{ ...CROSSHAIR_RING, left: `${crosshair.x}%`, top: `${crosshair.y}%` }} />
              </>
            )}
            {selections.map((sel, i) => sel.region ? null : (
              <Pin
                key={i}
                pin={sel}
                number={i + 1}
                status={pinStatus(sel)}
                label={`Pin ${i + 1} at ${describePin(sel)}. Press to remove.`}
                onActivate={interactive ? () => removeSelection(i) : null}
              />
            ))}
          </HotspotStage>

          {interactive && (
            <div className="d-flex align-items-center flex-wrap mt-2" style={{ gap: '0.5rem' }}>
              <span className="text-muted">
                Selected: {selections.length} of {maxSelections}
              </span>
              <Button
                className="me-btn me-btn-secondary me-btn-sm"
                onClick={() => { setMessage('Cleared all selections.'); save([]) }}
                disabled={isSaving || selections.length === 0}
              >
                Clear
              </Button>
              <Button
                className="me-btn-link-inline"
                aria-expanded={listOpen}
                onClick={() => setListOpen(open => !open)}
              >
                {listOpen ? 'Hide the list' : 'Choose from a list instead'}
              </Button>
            </div>
          )}

          {(interactive ? listOpen : listSelected.size > 0) && (
            <fieldset className="mt-2">
              <legend className="h6">{interactive ? 'Regions' : 'Chosen from the list'}</legend>
              {regions
                .filter(region => interactive || listSelected.has(region.guid))
                .map(region => {
                  const checked = listSelected.has(region.guid)
                  const status = reviewing ? getOptionFeedbackStatus(checked, correctSet.has(region.guid)) : null
                  return (
                    <div style={LIST_ROW} key={region.guid}>
                      <input
                        style={{ margin: 0 }}
                        type={maxSelections === 1 ? 'radio' : 'checkbox'}
                        name={`hotspot-list-${question.guid}`}
                        id={`hotspot-${question.guid}-${region.guid}`}
                        checked={checked}
                        disabled={!interactive || isSaving || (!checked && atMax && maxSelections > 1)}
                        onChange={() => toggleListRegion(region.guid)}
                      />
                      <label className="mb-0" htmlFor={`hotspot-${question.guid}-${region.guid}`}>
                        {region.label}
                        {status && status !== 'neutral' && (
                          <span className={`me-badge-tint me-badge-tint-${BADGE_FAMILY[status]} ml-2`}>{FEEDBACK_LABELS[status]}</span>
                        )}
                      </label>
                    </div>
                  )
                })}
            </fieldset>
          )}
        </div>
      </TabSplitLayout>
    </>
  )
}
